/**
 * @file AuthContext.tsx - Authentication Context Provider
 *
 * This module implements the authentication system for the Excalidraw application.
 * It provides:
 * - OAuth 2.0 authentication via external identity provider
 * - JWT verification using JWKS (JSON Web Key Set)
 * - Guest login support for users joining collaborative sessions
 * - Token expiration checking
 *
 * The authentication flow:
 * 1. User initiates login → redirected to external OAuth provider
 * 2. OAuth callback receives authorization code → exchanged for access token
 * 3. JWT verified against JWKS endpoint
 * 4. User info extracted from token and stored locally
 *
 * For guest access (joining rooms without full auth):
 * 1. User enters username
 * 2. Guest session created with local storage
 * 3. User can collaborate without full authentication
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { createRemoteJWKSet, jwtVerify, decodeJwt } from "jose";

import type { ReactNode } from "react";

/**
 * User object structure.
 * @property name - Display name of the user
 * @property isGuest - Whether user is authenticated via OAuth or joined as guest
 */
type User = {
  name: string;
  isGuest: boolean;
};

/**
 * Authentication context type definition.
 * Provides authentication state and methods for the entire application.
 *
 * @property isAuthenticated - True if user has valid JWT token
 * @property user - Current user object or null
 * @property token - JWT access token or null
 * @property error - Any authentication error that occurred
 * @property isExpired - Function to check if token is expired
 * @property login - Async function to verify and set JWT token
 * @property loginAsGuest - Function to create guest session
 * @property logout - Function to clear all auth state
 */
type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  error: Error | null;
  isExpired: () => boolean;
  login: (token: string) => Promise<void>;
  loginAsGuest: (username: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * LocalStorage keys for persisting authentication state.
 * Allows session persistence across page reloads.
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token", // JWT access token for authenticated users
  NAME: "name", // User display name from JWT claims
  GUEST_USERNAME: "guest_username", // Guest username for unauthenticated collaborators
} as const;

/**
 * JSON Web Key Set for verifying JWT signatures.
 * Fetches public keys from the Authentik JWKS endpoint.
 * Used by jwtVerify() to validate token authenticity.
 */
const JWKS = createRemoteJWKSet(
  new URL(`${import.meta.env.VITE_APP_JWKS_URI}`),
);

/**
 * Main authentication provider component.
 * Wraps the application and provides authentication context.
 *
 * Handles:
 * - Restoring auth session from localStorage on mount
 * - JWT verification via JWKS
 * - Guest session management
 * - Token expiration detection
 *
 * @param children - Child components that will have access to auth context
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
  );
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Restore guest session from localStorage if any
  useEffect(() => {
    const storedGuest = localStorage.getItem(STORAGE_KEYS.GUEST_USERNAME);
    const storedName = localStorage.getItem(STORAGE_KEYS.NAME);

    if (token && storedName) {
      // auth-based login
      setUser({ name: storedName, isGuest: false });
    } else if (storedGuest) {
      // guest login
      setUser({ name: storedGuest, isGuest: true });
    }
  }, [token]);

  const isAuthenticated = !!token;

  /**
   * Authenticates user with JWT token.
   * Verifies token signature against JWKS, extracts user info,
   * and persists session to localStorage.
   *
   * @param newToken - JWT access token from OAuth callback
   */
  const login = useCallback(async (newToken: string) => {
    try {
      const { payload } = await jwtVerify(newToken, JWKS);
      const name = payload.name as string;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newToken);
      localStorage.setItem(STORAGE_KEYS.NAME, name);

      setToken(newToken);
      setUser({ name, isGuest: false });
      setError(null);
    } catch (error: any) {
      console.error("Token verification failed:", error);
      setError(error);
    }
  }, []);

  /**
   * Creates a guest session for users joining collaborative rooms.
   * Does not require OAuth - allows read/write collaboration access.
   *
   * @param username - Display name for the guest user
   */
  const loginAsGuest = useCallback((username: string) => {
    localStorage.setItem(STORAGE_KEYS.GUEST_USERNAME, username);
    setToken(null);
    setUser({ name: username, isGuest: true });
    setError(null);
  }, []);

  /**
   * Clears all authentication state.
   * Removes tokens and user info from localStorage and context.
   */
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.NAME);
    localStorage.removeItem(STORAGE_KEYS.GUEST_USERNAME);

    setToken(null);
    setUser(null);
    setError(null);
  }, []);

  /**
   * Checks if the current JWT token is expired.
   * Decodes token without verification to check exp claim.
   *
   * @returns true if token is expired or invalid, false if valid
   */
  const isExpired = useCallback(() => {
    if (!token) {
      return true;
    }

    try {
      const payload = decodeJwt(token);
      const exp = payload.exp as number;
      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime >= exp;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return true;
    }
  }, [token]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      token,
      error,
      isExpired,
      login,
      loginAsGuest,
      logout,
    }),
    [
      isAuthenticated,
      user,
      token,
      error,
      isExpired,
      login,
      loginAsGuest,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication context.
 * Must be used within an AuthProvider.
 *
 * @returns AuthContextType with auth state and methods
 * @throws Error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

/**
 * Handles OAuth callback by exchanging authorization code for access token.
 * Called from CallbackPage after user returns from OAuth provider.
 *
 * Flow:
 * 1. Receives authorization code from URL
 * 2. Sends code to backend /auth/token endpoint
 * 3. Backend exchanges code with OAuth provider for tokens
 * 4. Returns access token to frontend
 *
 * @param code - Authorization code from OAuth callback URL
 * @returns JWT access token string
 * @throws Error if token exchange fails
 */
export const handleAuthCallback = async (code: string) => {
  const response = await fetch(
    `${import.meta.env.VITE_APP_HTTP_BACKEND_URL}auth/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "plain/text",
      },
      body: code,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch access token");
  }

  const data = await response.json();
  return data.access_token;
};
